/**
 * Arc Profile Registry Service
 *
 * Reads/writes the per-wallet IPFS CID on the Arc Testnet PeopleTag contract.
 */

import { ethers } from 'ethers';
import { ARC_TESTNET_NETWORK } from '../config/blockchain';

const CONTRACT_ABI = [
  "function profileCID(address user) external view returns (string memory)",
  "function setProfileCID(string memory cid) external"
];

/**
 * Save CID to Arc Testnet (requires MetaMask, switches network if needed).
 * @param {string} cid
 * @returns {Promise<string>} transaction hash
 */
export const saveCIDToArc = async (cid) => {
  if (!window.ethereum) {
    throw new Error('MetaMask provider not available');
  }

  let provider = new ethers.BrowserProvider(window.ethereum);
  let network = await provider.getNetwork();

  if (Number(network.chainId) !== ARC_TESTNET_NETWORK.chainIdDecimal) {
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: ARC_TESTNET_NETWORK.chainId }],
      });
    } catch (switchError) {
      if (switchError.code === 4902) {
        await window.ethereum.request({
          method: 'wallet_addEthereumChain',
          params: [{
            chainId: ARC_TESTNET_NETWORK.chainId,
            chainName: ARC_TESTNET_NETWORK.name,
            rpcUrls: [ARC_TESTNET_NETWORK.rpcUrl],
            blockExplorerUrls: [ARC_TESTNET_NETWORK.blockExplorer],
            nativeCurrency: ARC_TESTNET_NETWORK.nativeCurrency
          }],
        });
      } else {
        throw switchError;
      }
    }

    let attempts = 0;
    const maxAttempts = 10;
    while (attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 300));
      provider = new ethers.BrowserProvider(window.ethereum);
      network = await provider.getNetwork();
      if (Number(network.chainId) === ARC_TESTNET_NETWORK.chainIdDecimal) break;
      attempts++;
    }

    if (Number(network.chainId) !== ARC_TESTNET_NETWORK.chainIdDecimal) {
      throw new Error(`Network switch failed. Expected Arc Testnet (${ARC_TESTNET_NETWORK.chainIdDecimal}), but got ${network.chainId}. Please switch to Arc Testnet manually in MetaMask.`);
    }
  }

  const signer = await provider.getSigner();
  const contract = new ethers.Contract(
    ARC_TESTNET_NETWORK.contractAddress,
    CONTRACT_ABI,
    signer
  );

  try {
    const tx = await contract.setProfileCID(cid);
    await tx.wait();
    return tx.hash;
  } catch (txError) {
    if (txError.message && txError.message.includes('network changed')) {
      throw new Error(`Transaction failed: network changed during send. Please ensure you're on Arc Testnet (${ARC_TESTNET_NETWORK.chainIdDecimal}) in MetaMask and try again.`);
    }
    throw txError;
  }
};

/**
 * Get CID from Arc Testnet (read-only via JSON-RPC, no MetaMask switching).
 * @param {string} walletAddress
 * @returns {Promise<string|null>}
 */
export const getCIDFromArc = async (walletAddress) => {
  try {
    const provider = new ethers.JsonRpcProvider(ARC_TESTNET_NETWORK.rpcUrl);
    const contract = new ethers.Contract(
      ARC_TESTNET_NETWORK.contractAddress,
      CONTRACT_ABI,
      provider
    );

    const cid = await Promise.race([
      contract.profileCID(walletAddress),
      new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 5000))
    ]);

    return cid && cid !== '' && cid !== '0x' ? cid : null;
  } catch (error) {
    console.error('Error getting CID from Arc:', error);
    return null;
  }
};
